// AuthenticatedApp.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchUserContext } from './utils/authService';
import Loading from "./components/Loading";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const AuthenticatedApp = ({ children }) => {
  const { user, isAuthenticated, isLoading, loginWithRedirect, logout, getAccessTokenSilently } = useAuth0();
  const [userContext, setUserContext] = useState(null);
  const [isCheckingRole, setIsCheckingRole] = useState(true);

  const fetchUserContextFromBackend = async () => {
    try {
      const context = await fetchUserContext(getAccessTokenSilently);
      setUserContext(context);
    } catch (error) {
      // If user not found in database, logout
      logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    }
  };

  const checkAuthenticationAndFetchContext = async () => {
    try {
      if (!isLoading) {
        if (!isAuthenticated) {
          await loginWithRedirect();
        } else if (user) {
          await fetchUserContextFromBackend();
          setIsCheckingRole(false);
        }
      }
    } catch (error) {
      logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    }
  };

  // Helper function to check if user has a specific permission
  const hasPermission = (permissionName) => {
    return userContext?.permissions?.some(
      permission => permission.name === permissionName
    ) || false;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    checkAuthenticationAndFetchContext();
  }, [isLoading, isAuthenticated, user]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isCheckingRole && isAuthenticated && userContext) {
      // Check if user has access_admin_panel permission
      if (!hasPermission("access_admin_panel")) {
        logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });
      }
    }
  }, [isCheckingRole, isAuthenticated, userContext, logout]);

  if (isLoading || isCheckingRole) {
    return <Loading />;
  }

  // Check if user has access_admin_panel permission
  if (!hasPermission("access_admin_panel")) {
    return null;
  }

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      userRole: userContext.role,
      userPermissions: userContext.permissions || [],
      venueId: userContext.venueId,
      hasPermission
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthenticatedApp;
